import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import OrdersTable from '../../components/orders/OrdersTable';
import OrderDetailsModal from '../../components/orders/OrderDetailsModal';
import QuickUploadModal from '../../components/orders/QuickUploadModal';
import { fetchOrders } from '../../store/slices/orderSlice';
import { useOrderDetails } from '../../hooks/useOrderDetails';
import getOrderColumns from './columns';

const addSerialNumbers = (data, page, rowsPerPage) =>
  data.map((row, idx) => ({
    ...row,
    sn: (page * rowsPerPage) + idx + 1
  }));

const PurchasedOrders = () => {
  console.log('PurchasedOrders component rendered');
  const { t } = useTranslation(['orders', 'common']);
  const dispatch = useDispatch();
  const { orders, loading, totalOrders } = useSelector((state) => state.order);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filterUsername, setFilterUsername] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterBinCode, setFilterBinCode] = useState('');
  const [filterWarehouseNumber, setFilterWarehouseNumber] = useState('');
  const [filterOrderNumber, setFilterOrderNumber] = useState('');
  const [filterPurchasingAccount, setFilterPurchasingAccount] = useState('');
  const [filterPoolId, setFilterPoolId] = useState('');
  const { selectedOrderId, modalOpen, handleView, handleCloseModal } = useOrderDetails();
  const [quickUploadModalOpen, setQuickUploadModalOpen] = useState(false);
  const [selectedOrderForUpload, setSelectedOrderForUpload] = useState(null);
  const totalPages = Math.ceil(totalOrders / rowsPerPage) || 1;

  useEffect(() => {
    console.log('useEffect triggered with filters:', { filterUsername, filterPhone, filterBinCode, filterWarehouseNumber, filterOrderNumber, filterPurchasingAccount, filterPoolId });

    const params = {
      status: 3,
      page: page + 1,
      page_size: rowsPerPage,
      username: filterUsername || undefined,
      phone: filterPhone || undefined,
      bin_code: filterBinCode || undefined,
      warehouse_number: filterWarehouseNumber || undefined,
      order_number: filterOrderNumber || undefined,
      purchasing_account: filterPurchasingAccount || undefined,
      group_buy_pool_id: filterPoolId || undefined,
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    console.log('Sending API request with params:', params);
    dispatch(fetchOrders(params));
  }, [dispatch, page, rowsPerPage, filterUsername, filterPhone, filterBinCode, filterWarehouseNumber, filterOrderNumber, filterPurchasingAccount, filterPoolId]);

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Filter handlers
  const handleFilterUsernameChange = (e) => {
    console.log('handleFilterUsernameChange called with:', e.target.value);
    setFilterUsername(e.target.value);
  };
  const handleFilterPhoneChange = (e) => {
    console.log('handleFilterPhoneChange called with:', e.target.value);
    setFilterPhone(e.target.value);
  };
  const handleFilterBinCodeChange = (e) => {
    console.log('handleFilterBinCodeChange called with:', e.target.value);
    setFilterBinCode(e.target.value);
  };
  const handleFilterWarehouseNumberChange = (e) => {
    console.log('handleFilterWarehouseNumberChange called with:', e.target.value);
    setFilterWarehouseNumber(e.target.value);
  };
  const handleFilterOrderNumberChange = (e) => {
    console.log('handleFilterOrderNumberChange called with:', e.target.value);
    setFilterOrderNumber(e.target.value);
  };
  const handleFilterPurchasingAccountChange = (e) => {
    console.log('handleFilterPurchasingAccountChange called with:', e.target.value);
    setFilterPurchasingAccount(e.target.value);
  };
  const handleFilterPoolIdChange = (e) => {
    console.log('handleFilterPoolIdChange called with:', e.target.value);
    setFilterPoolId(e.target.value);
  };

  // Action handlers
  const handleUpload = (row) => {
    setSelectedOrderForUpload(row);
    setQuickUploadModalOpen(true);
  };

  const handleCloseQuickUploadModal = () => {
    setQuickUploadModalOpen(false);
    setSelectedOrderForUpload(null);
  };

  const handleUploadSuccess = (uploadData) => {
    // Refresh orders list to show updated status with current filters
    const params = {
      status: 3,
      page: page + 1,
      page_size: rowsPerPage,
      username: filterUsername || undefined,
      phone: filterPhone || undefined,
      bin_code: filterBinCode || undefined,
      warehouse_number: filterWarehouseNumber || undefined,
      order_number: filterOrderNumber || undefined,
      purchasing_account: filterPurchasingAccount || undefined,
      group_buy_pool_id: filterPoolId || undefined,
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    dispatch(fetchOrders(params));
    handleCloseQuickUploadModal();
  };

  const columns = getOrderColumns({ onView: handleView, onUpload: handleUpload, t });

  return (
    <>
      <OrdersTable
        orders={addSerialNumbers(orders, page, rowsPerPage)}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalPages={totalPages}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        setCustomRowsActive={setCustomRowsActive}
        setCustomRowsInput={setCustomRowsInput}
        handleCustomRowsInputChange={handleCustomRowsInputChange}
        handleCustomRowsInputBlur={handleCustomRowsInputBlur}
        columns={columns}
        filterUsername={filterUsername}
        filterPhone={filterPhone}
        filterBinCode={filterBinCode}
        filterWarehouseNumber={filterWarehouseNumber}
        filterOrderNumber={filterOrderNumber}
        filterPurchasingAccount={filterPurchasingAccount}
        filterPoolId={filterPoolId}
        onFilterUsernameChange={handleFilterUsernameChange}
        onFilterPhoneChange={handleFilterPhoneChange}
        onFilterBinCodeChange={handleFilterBinCodeChange}
        onFilterWarehouseNumberChange={handleFilterWarehouseNumberChange}
        onFilterOrderNumberChange={handleFilterOrderNumberChange}
        onFilterPurchasingAccountChange={handleFilterPurchasingAccountChange}
        onFilterPoolIdChange={handleFilterPoolIdChange}
      />
      <OrderDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        orderId={selectedOrderId}
      />
      <QuickUploadModal
        open={quickUploadModalOpen}
        onClose={handleCloseQuickUploadModal}
        orderId={selectedOrderForUpload?.id}
        orderNumber={selectedOrderForUpload?.order_number}
        onUploadSuccess={handleUploadSuccess}
      />
    </>
  );
};

export default PurchasedOrders; 
